import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { AssetsUser, ELECTRICAL_ASPECT_TYPE, ElectricalAspect } from 'assets-model';
import { ElectricalAspectDataSource, ElectricalAspectService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class ElectricalAspectSearchComponentBase extends CrudListComponent<ElectricalAspect, number> {
  protected systemConfig: any;

  displayedColumns: string[] = [ 'aspectAssetId', 'lockVersion', 'portableApplianceTestingDays', 'actions'];

  public searchForm: FormGroup;

  aspectAssetIdFilter: number;
  @ViewChild('aspectAssetIdFilterInput') aspectAssetIdFilterInput: ElementRef;

  portableApplianceTestingDaysFilter: number;
  @ViewChild('portableApplianceTestingDaysFilterInput') portableApplianceTestingDaysFilterInput: ElementRef;
  portableApplianceTestingDaysLessThanFilter: number;
  @ViewChild('portableApplianceTestingDaysLessThanFilterInput') portableApplianceTestingDaysLessThanFilterInput: ElementRef;
  portableApplianceTestingDaysLessThanOrEqualToFilter: number;
  @ViewChild('portableApplianceTestingDaysLessThanOrEqualToFilterInput') portableApplianceTestingDaysLessThanOrEqualToFilterInput: ElementRef;
  portableApplianceTestingDaysGreaterThanFilter: number;
  @ViewChild('portableApplianceTestingDaysGreaterThanFilterInput') portableApplianceTestingDaysGreaterThanFilterInput: ElementRef;
  portableApplianceTestingDaysGreaterThanOrEqualToFilter: number;
  @ViewChild('portableApplianceTestingDaysGreaterThanOrEqualToFilterInput') portableApplianceTestingDaysGreaterThanOrEqualToFilterInput: ElementRef;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected electricalAspectService: ElectricalAspectService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(ELECTRICAL_ASPECT_TYPE, route, router, userStorage, dialog, electricalAspectService, injector);

    this.searchForm = this.fb.group({
      aspectAssetId: [null],
      portableApplianceTestingDaysLessThan: [null],
      portableApplianceTestingDaysLessThanOrEqualTo: [null],
      portableApplianceTestingDaysGreaterThan: [null],
      portableApplianceTestingDaysGreaterThanOrEqualTo: [null],
      portableApplianceTestingDays: [null]
    });
  }

  createDataSource(): CrudDataSource<ElectricalAspect> {
    return new ElectricalAspectDataSource(this.electricalAspectService);
  }

  createServiceArguments(): any {
    return {
      aspectAssetId: this.aspectAssetIdFilter,
      portableApplianceTestingDaysLessThan: this.portableApplianceTestingDaysLessThanFilter,
      portableApplianceTestingDaysLessThanOrEqualTo: this.portableApplianceTestingDaysLessThanOrEqualToFilter,
      portableApplianceTestingDaysGreaterThan: this.portableApplianceTestingDaysGreaterThanFilter,
      portableApplianceTestingDaysGreaterThanOrEqualTo: this.portableApplianceTestingDaysGreaterThanOrEqualToFilter,
      portableApplianceTestingDays: this.portableApplianceTestingDaysFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.aspectAssetIdFilter = null;
    this.portableApplianceTestingDaysLessThanFilter = null;
    this.portableApplianceTestingDaysLessThanOrEqualToFilter = null;
    this.portableApplianceTestingDaysGreaterThanFilter = null;
    this.portableApplianceTestingDaysGreaterThanOrEqualToFilter = null;
    this.portableApplianceTestingDaysFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.aspectAssetIdFilterInput);
    this.addSearchOnChangedInputEventListener(this.portableApplianceTestingDaysLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.portableApplianceTestingDaysLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.portableApplianceTestingDaysGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.portableApplianceTestingDaysGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.portableApplianceTestingDaysFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      aspectAssetId: (value: number) => self.aspectAssetIdFilter = value,
      portableApplianceTestingDaysLessThan: (value: number) => self.portableApplianceTestingDaysLessThanFilter = value,
      portableApplianceTestingDaysLessThanOrEqualTo: (value: number) => self.portableApplianceTestingDaysLessThanOrEqualToFilter = value,
      portableApplianceTestingDaysGreaterThan: (value: number) => self.portableApplianceTestingDaysGreaterThanFilter = value,
      portableApplianceTestingDaysGreaterThanOrEqualTo: (value: number) => self.portableApplianceTestingDaysGreaterThanOrEqualToFilter = value,
      portableApplianceTestingDays: (value: number) => self.portableApplianceTestingDaysFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }

  aspectAssetIdChanging(value: number, unsetDependents: boolean = true) {}
  
  aspectAssetIdChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  portableApplianceTestingDaysChanging(value: number, unsetDependents: boolean = true) {}
  
  portableApplianceTestingDaysChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  portableApplianceTestingDaysLessThanChanging(value: number, unsetDependents: boolean = true) {}
  
  portableApplianceTestingDaysLessThanChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  portableApplianceTestingDaysLessThanOrEqualToChanging(value: number, unsetDependents: boolean = true) {}
  
  portableApplianceTestingDaysLessThanOrEqualToChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  portableApplianceTestingDaysGreaterThanChanging(value: number, unsetDependents: boolean = true) {}
  
  portableApplianceTestingDaysGreaterThanChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  portableApplianceTestingDaysGreaterThanOrEqualToChanging(value: number, unsetDependents: boolean = true) {}
  
  portableApplianceTestingDaysGreaterThanOrEqualToChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get aspectAssetId() {
    return this.searchForm && this.searchForm.get('aspectAssetId');
  }

  get portableApplianceTestingDays() {
    return this.searchForm && this.searchForm.get('portableApplianceTestingDays');
  }

  get portableApplianceTestingDaysLessThan() {
    return this.searchForm && this.searchForm.get('portableApplianceTestingDaysLessThan');
  }
  
  get portableApplianceTestingDaysLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('portableApplianceTestingDaysLessThanOrEqualTo');
  }
  
  get portableApplianceTestingDaysGreaterThan() {
    return this.searchForm && this.searchForm.get('portableApplianceTestingDaysGreaterThan');
  }
  
  get portableApplianceTestingDaysGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('portableApplianceTestingDaysGreaterThanOrEqualTo');
  }


}



